/* oxlint-disable @typescript-eslint/ban-ts-comment */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NetworkConnection from 'NetworkConnection';
import { useTranslation } from 'react-i18next';
import isChromatic from 'chromatic';
import { createPortal } from 'react-dom';
import { FlatButton } from 'FlatButton';
import { ModalBackdrop } from 'FrontRoyalModal';
import { type ErrorHandlingComponent, type RetryFunction } from '../../FrontRoyalErrorBoundary.types';

type Props = {
    retry: RetryFunction;
    closeModal: () => void;
};

const BASE_LOCALE = 'front_royal_api_error_handler.disconnected';
const timerSeconds = 10;

function useNetworkConnectionOnline() {
    const [online, setOnline] = useState(NetworkConnection.online);

    useEffect(() => {
        const id = setInterval(() => {
            setOnline(NetworkConnection.online);
        }, 500);

        return () => {
            clearInterval(id);
        };
    }, []);

    return online;
}

// I'm not sure this is going to work well with the kind of retry
// function we're going to get from DataLoader, for example, but we'll
// see
function useCloseModalOnRetrySuccess({
    closeModal,
    retrySuccess,
}: {
    closeModal: Props['closeModal'];
    retrySuccess: boolean;
}) {
    useEffect(() => {
        if (retrySuccess) {
            closeModal();
        }
    }, [closeModal, retrySuccess]);
}

function useRetryTimer({
    online,
    isRetrying,
    setIsRetrying,
    setRetrySuccess,
    retry,
}: {
    online: boolean;
    retry: RetryFunction;
    setIsRetrying: (isRetrying: boolean) => void;
    setRetrySuccess: (success: boolean) => void;
    isRetrying: boolean;
}) {
    const [timeUntilNextRetry, setTimeUntilNextRetry] = useState<number | null>(null);
    const previousOnline = useRef(NetworkConnection.online);

    useEffect(() => {
        const justCameOnline = !previousOnline.current && online;
        previousOnline.current = online;

        // When the timer hits 0, retry
        if (online && timeUntilNextRetry === 0 && !isRetrying) {
            retryAndSetRetrySuccess({ retry, setIsRetrying, setRetrySuccess });

            // As long as you are offline, there is no timer
            // When a retry is in flight, there is no timer
        } else if (!online || isRetrying) {
            setTimeUntilNextRetry(null);

            // When coming online, trigger an (almost) immediate retry.
            // Wait a second because maybe that makes it more likely to work
        } else if (justCameOnline) {
            setTimeUntilNextRetry(1);

            // If you are online, not retrying, and there is no timer, then start the timer
        } else if (online && !isRetrying && !timeUntilNextRetry) {
            setTimeUntilNextRetry(timerSeconds);
        }
    }, [online, timeUntilNextRetry, isRetrying, retry, setIsRetrying, setRetrySuccess]);

    // Countdown the timer
    useEffect(() => {
        const id = setTimeout(() => {
            if (timeUntilNextRetry) setTimeUntilNextRetry(timeUntilNextRetry - 1);
        }, 1000);

        return () => clearTimeout(id);
    }, [timeUntilNextRetry, setTimeUntilNextRetry]);

    return { setTimeUntilNextRetry, timeUntilNextRetry };
}

async function retryAndSetRetrySuccess({
    retry,
    setIsRetrying,
    setRetrySuccess,
}: {
    retry: RetryFunction;
    setIsRetrying: (isRetrying: boolean) => void;
    setRetrySuccess: (retrySuccess: boolean) => void;
}) {
    setIsRetrying(true);
    try {
        await retry();
        setRetrySuccess(true);
    } catch {
        setIsRetrying(false);
    }
}

function useButtonText({ online, isRetrying }: { online: boolean; isRetrying: boolean }) {
    const { t } = useTranslation('back_royal');
    return useMemo(() => {
        if (!online) return t(`${BASE_LOCALE}.reconnecting`);
        if (isRetrying) return t(`${BASE_LOCALE}.cancel`);

        return t(`${BASE_LOCALE}.retry_now`);
    }, [t, isRetrying, online]);
}

function useOnClick({
    isRetrying,
    setIsRetrying,
    setTimeUntilNextRetry,
}: {
    isRetrying: boolean;
    setIsRetrying: (isRetrying: boolean) => void;
    setTimeUntilNextRetry: (time: number) => void;
}) {
    return useCallback(() => {
        // Note that nothing can actually stop the retry that is in flight. If someone
        // cancels a retry and starts another, those will both be in flight at the same time.
        // It's possible that both will finish and update the UI. But, without knowing what's going
        // on inside the retry function, we can't prevent that, and it's preferable to giving no
        // way to cancel a hanging request and trigger a new one.
        if (isRetrying) {
            setIsRetrying(false);
        } else {
            setTimeUntilNextRetry(0);
        }
    }, [isRetrying, setTimeUntilNextRetry, setIsRetrying]);
}

function useStatusText({
    online,
    isRetrying,
    timeUntilNextRetry,
}: {
    online: boolean;
    isRetrying: boolean;
    timeUntilNextRetry: number | null;
}) {
    const { t } = useTranslation('back_royal');
    return useMemo(() => {
        if (!online) return t(`${BASE_LOCALE}.we_will_try_again_when_you_are_online`);

        if (isRetrying) return t(`${BASE_LOCALE}.request_has_been_sent`);

        if (timeUntilNextRetry !== null) {
            return t(`${BASE_LOCALE}.will_retry_at_react`, { seconds: isChromatic() ? 10 : timeUntilNextRetry });
        }

        return null;
    }, [isRetrying, online, timeUntilNextRetry, t]);
}

const DisconnectedErrorModal = ({ retry, closeModal }: Props) => {
    const online = useNetworkConnectionOnline();
    const [isRetrying, setIsRetrying] = useState(false);
    const [retrySuccess, setRetrySuccess] = useState(false);
    const { setTimeUntilNextRetry, timeUntilNextRetry } = useRetryTimer({
        online,
        isRetrying,
        setIsRetrying,
        setRetrySuccess,
        retry,
    });
    const buttonText = useButtonText({ online, isRetrying });
    const onClick = useOnClick({ isRetrying, setIsRetrying, setTimeUntilNextRetry });
    const status = useStatusText({ online, isRetrying, timeUntilNextRetry });
    const { t } = useTranslation('back_royal');
    useCloseModalOnRetrySuccess({ closeModal, retrySuccess });

    return (
        <ModalBackdrop>
            <div className="absolute left-0 top-0 z-[10100] flex h-[75px] w-full bg-white font-semibold text-eggplant shadow-[0_3px_9px_rgba(0,0,0,0.5)] sm:left-1/2 sm:h-[50px] sm:w-[600px] sm:translate-x-[-50%] sm:transform sm:rounded-bl-[5px] sm:rounded-br-[5px]">
                <div className="flex w-full items-center justify-center p-1">
                    <span className="text-center text-[13px] xs:text-[16px]">
                        {!isRetrying && <span>{t(`${BASE_LOCALE}.a_request_failed`)}</span>}{' '}
                        {!!status && <span>{status}</span>}
                    </span>
                </div>

                <FlatButton
                    onClick={onClick}
                    className="flex-none basis-[110px] rounded-none bg-[rgba(0,0,0,0.1)] px-[5px] py-[10px] text-[13px] font-semibold text-eggplant"
                    disabled={NetworkConnection.offline}
                >
                    {buttonText}
                </FlatButton>
                <div className="flex flex-none basis-[35px] items-center justify-center">
                    {(isRetrying || !online || timeUntilNextRetry !== null) && (
                        <i className="fa fa-refresh fa-spin loading-spinner text-lg" />
                    )}
                </div>
            </div>
        </ModalBackdrop>
    );
};

export function ShowDisconnectedModal({
    retry,
    resetErrorBoundary,
}: {
    retry: Parameters<ErrorHandlingComponent>[0]['retry'];
    resetErrorBoundary: Parameters<ErrorHandlingComponent>[0]['resetErrorBoundary'];
}) {
    if (!retry) throw new Error('Retry function is required');

    return <>{createPortal(<DisconnectedErrorModal retry={retry} closeModal={resetErrorBoundary} />, document.body)}</>;
}
